import { createLink } from '@/helpers/blob'
import { getFileExtension, goToDocumentRoute } from '@/helpers/file'
import store from '@/store/store'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { openLefebvrePdfViewer } from '@/helpers/pdfViewer'
import { URLS } from '@/router/routes/urlRoutes'
import { isSafari } from '@/helpers/navigator'
import useMSOffice from './useMSOffice'
import useWopi from './useWopi'
import { openUrl } from '@/helpers/url'
import usePermissions from '@/composables/usePermissions'
import { documentEntityId } from '@/general/entityIds'

interface ResponseUseFiles {
  openFile: (file: FileData) => void
  openInLocation: (file: FileData) => void
  downloadFile: (file: FileData) => Promise<boolean>
  isOnlineEditable: (fileName: string) => boolean
}

export interface FileData {
  id: number
  originalFileName: string
  name?: string | null
  extension?: string
  type?: string
  source?: string
  url?: string
}

export const DOWNLOAD_EXTENSION = ['msg', '.eml']
export const ONLINE_EDIT_EXTENSION = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'rtf']

export default function useFiles(): ResponseUseFiles {
  const { isMSOfficeLocal, openMSOfficeLocal } = useMSOffice()

  const { editDocumentOnline } = useWopi()

  const { checkEntityPermissionsGetter } = usePermissions()

  const canEditDocument = (): boolean => {
    const { canSave } = checkEntityPermissionsGetter(documentEntityId)
    return !!canSave
  }

  const execDownloadFile = async (id: string) =>
    await store.dispatch(`${ModuleNamespaces.FILE_MANAGER}/downloadFile`, id)

  const openFile = (file: FileData) => {
    if (file.source === 'url' && file.url) {
      openUrl(file.url)
      return
    }
    const extension = getFileExtension(file.originalFileName)
    if (extension === 'pdf') {
      if (canEditDocument()) {
        openLefebvrePdfViewer(file.id)
      } else {
        downloadFile(file)
      }
      return
    }

    if (isOnlineEditable(extension)) {
      openOnlineEditable(file)
      return
    }

    downloadFile(file)
  }

  const isOnlineEditable = (extension: string) => {
    return ONLINE_EDIT_EXTENSION.includes(extension.toLowerCase())
  }

  const isRtfFile = (file: FileData): boolean => {
    const extension = getFileExtension(file.originalFileName)
    return extension.toLowerCase() === 'rtf'
  }

  const openOnlineEditable = (file: FileData) => {
    if (!canEditDocument()) {
      downloadFile(file)
      return
    }

    // if (local)
    if (isMSOfficeLocal(file)) {
      openMSOfficeLocal(file)
      return
    }

    // RTF always opens via WebDAV
    if (isRtfFile(file)) {
      downloadFile(file)
      return
    }

    // if (wopi)
    editDocumentOnline(file)
  }

  const downloadFile = async (file: FileData): Promise<boolean> => {
    try {
      // probar en safari
      if (isSafari()) {
        const url = `/${URLS.REPOSITORY}?fileId=${file.id}`
        createLink(url, '', true)
        return true
      }

      const blobFile = await execDownloadFile(file.id.toString())
      const blobUrl = URL.createObjectURL(blobFile)
      createLink(blobUrl, file.originalFileName, true)
      return true
    } catch (error) {
      return false
    }
  }

  const openInLocation = async (file: FileData) => {
    const pathInfo = await store.dispatch(`${ModuleNamespaces.FILE_MANAGER}/fetchDocumentPathInfo`, file.id)
    const route = goToDocumentRoute(file, pathInfo)
    window.open(route, '_blank')
  }

  return {
    openFile,
    downloadFile,
    isOnlineEditable,
    openInLocation
  }
}
